import { API_ENDPOINTS, api } from "../apis/constants";
import { CustomBox, Product, ProductInBox } from "../types/product";
import { cacheService } from "./cache.service";

const normalizeProductInBox = (product: Record<string, unknown>): ProductInBox => {
    const nested =
        product.id && typeof product.id === "object"
            ? (product.id as Record<string, unknown>)
            : null;
    const source = nested ?? product;
    const pricing =
        source.pricing && typeof source.pricing === "object"
            ? (source.pricing as Record<string, unknown>)
            : null;

    return {
        id: String(source._id ?? source.id ?? product.id ?? ""),
        name: String(source.name ?? product.name ?? ""),
        images: (source.images ?? product.images ?? []) as string[],
        unit: String(source.unit ?? product.unit ?? ""),
        status: String(source.status ?? product.status ?? "active"),
        min_quantity: Number(product.min_quantity ?? source.min_quantity ?? 0),
        max_quantity: Number(product.max_quantity ?? source.max_quantity ?? 99),
        description: String(source.description ?? product.description ?? ""),
        type: String(product.type ?? source.type ?? ""),
        original_price: Number(
            source.original_price ??
                pricing?.original_price ??
                product.original_price ??
                0
        ),
        sale_price: Number(
            source.sale_price ??
                pricing?.sale_price ??
                product.sale_price ??
                0
        ),
        weight_per_product: Number(
            product.weight_per_product ?? source.weight_per_product ?? 0
        ),
    };
};

const normalizeCustomBox = (data: unknown): CustomBox => {
    if (!data || typeof data !== "object") {
        throw new Error("Dữ liệu hộp quà không hợp lệ");
    }

    const raw = data as Record<string, unknown>;
    const box =
        raw.data && typeof raw.data === "object"
            ? (raw.data as Record<string, unknown>)
            : raw.box_template && typeof raw.box_template === "object"
              ? (raw.box_template as Record<string, unknown>)
              : raw;

    const id = String(box.id ?? box._id ?? "");
    if (!id) {
        throw new Error("Dữ liệu hộp quà không hợp lệ");
    }

    return {
        id,
        name: String(box.name ?? ""),
        description: String(box.description ?? ""),
        images: (box.images as string[]) ?? [],
        products: Array.isArray(box.products)
            ? box.products.map((product) =>
                  normalizeProductInBox(product as Record<string, unknown>)
              )
            : [],
        created_at: box.created_at as string | undefined,
        updated_at: box.updated_at as string | undefined,
        slug: box.slug as string | undefined,
        initial_price: Number(box.initial_price ?? 0),
        min_weight: Number(box.min_weight ?? 400),
    };
};

export interface ProductListResponse {
    products: ProductItem[];
    total: number;
    page: number;
    totalPages: number;
}

export interface ProductVariant {
    pricing: {
        original_price: number;
    };
    name: string;
    description?: string;
    images?: string[];
    status: string;
    _id: string;
}

export interface ProductItem {
    id: string;
    name: string;
    description: string;
    category: string;
    images?: string[];
    unit: string;
    original_price: number;
    current_price: number;
    sale_price?: number;
    is_on_sale: boolean;
    is_trending: boolean;
    // images?: string[];
    slug?: string;
    status?: string;
    type?: string;
    variants?: ProductVariant[];
}

interface ProductQueryParams {
    page?: number;
    limit?: number;
    search?: string;
    categoryslug?: string;
    sort?: string;
}

interface CacheOptions {
    forceFresh?: boolean;
    cacheTTL?: number;
}

export interface SpecialOrderProductResponse {
    box_templates: BoxTemplate[];
    pagination: Pagination;
}

export interface BoxTemplate {
    _id: string;
    name: string;
    description: string;
    type: string;
    status: string;
    min_weight: number;
    initial_price: number;
    slug: string;
    products: BoxProduct[];
    images?: string[];
    created_at: string;
    updated_at: string;
}

export interface BoxProduct {
    id: SubProduct;
    type: string;
    min_quantity: number;
    max_quantity: number;
    _id?: string;
}

export interface SubProduct {
    _id: string;
    name: string;
    description: string;
    unit: string;
    status: string;
    images?: string[];
    pricing: {
        original_price: number;
        sale_price: number;
        sale_start_date: string | null;
        sale_end_date: string | null;
    };
}

export interface Pagination {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
}

export interface BoxTemplateResponse {
    box_templates: BoxTemplate[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        total_pages: number;
    };
}

export interface BoxTemplateParams {
    page?: number;
    limit?: number;
}

class ProductService {
    private PRODUCTS_CACHE_KEY_PREFIX = 'products_';
    private SPECIAL_ORDERS_CACHE_KEY = 'special_orders';
    private PRODUCT_CACHE_TTL = 60; // 1 minute in seconds

    async getProductDetail(id: string): Promise<Product> {
        try {
            const response = await api.get<Product>(`${API_ENDPOINTS.PRODUCTS.DETAIL}/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async getCustomBoxDetail(slug: string): Promise<CustomBox> {
        if (!slug?.trim()) {
            throw new Error("Slug sản phẩm không hợp lệ");
        }

        try {
            const response = await api.get(
                `${API_ENDPOINTS.PRODUCTS.SPECIAL_ORDER_DETAIL}/${slug}`
            );
            return normalizeCustomBox(response.data);
        } catch (error) {
            throw error;
        }
    }

    async getAllProducts(params: ProductQueryParams = {}, cacheOptions: CacheOptions = {}): Promise<ProductListResponse> {
        const { page = 1, limit = 12, search, categoryslug, sort } = params;

        // Build query parameters
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());

        if (search) {
            queryParams.append('search', search);
        }

        if (categoryslug) {
            queryParams.append('category_slug', categoryslug);
        }

        if (sort) {
            queryParams.append('sort', sort);
        }

        // Create a unique cache key based on query parameters
        const queryString = queryParams.toString();
        const cacheKey = `${this.PRODUCTS_CACHE_KEY_PREFIX}${queryString}`;

        // Force fresh data for now to debug
        try {
            const response = await api.get(`${API_ENDPOINTS.PRODUCTS.ALL}?${queryString}`);
            // console.log('API Response for getAllProducts:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching products:', error);
            throw error;
        }
    }

    async getProductsByCategory(params: ProductQueryParams = {}, cacheOptions: CacheOptions = {}): Promise<ProductListResponse> {
        const { page = 1, limit = 12, search, categoryslug, sort } = params;
        return cacheService.get<ProductListResponse>(
            '',
            async () => {
                try {
                    const response = await api.get(`${API_ENDPOINTS.PRODUCTS.CATEGORY}/${categoryslug}`);
                    return response.data;
                } catch (error) {
                    console.error('Error fetching products:', error);
                    throw error;
                }
            },
            {
                forceFresh: cacheOptions.forceFresh,
                ttl: cacheOptions.cacheTTL || this.PRODUCT_CACHE_TTL
            }
        );
    }

    async getSpecialOrderProducts(params?: BoxTemplateParams): Promise<BoxTemplateResponse> {
        try {
            let url = `${API_ENDPOINTS.PRODUCTS.SPECIAL_ORDER}`;

            // Add query parameters if provided
            if (params) {
                const queryParams = new URLSearchParams();
                if (params.page) queryParams.append('page', params.page.toString());
                if (params.limit) queryParams.append('limit', params.limit.toString());

                if (queryParams.toString()) {
                    url += `?${queryParams.toString()}`;
                }
            }

            const response = await api.get<BoxTemplateResponse>(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching special order products:', error);
            throw error;
        }
    }

    async getSpecialOrderDetail(slug: string): Promise<BoxTemplate> {
        try {
            const response = await api.get<BoxTemplate>(`${API_ENDPOINTS.PRODUCTS.SPECIAL_ORDER_DETAIL}/${slug}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
}
export const productService = new ProductService();
